import cx from 'classnames';
import { lazy, memo, Suspense, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import {
  ADMIN_MY_PRODUCTS_3DMODAL_V2,
  ADMIN_MY_PRODUCTS_TEXTURE_V2,
} from '../../constants/routePathConstants';
import Breadcrumbs from '../../layouts/Breadcrumbs';
import { useParams } from 'react-router-dom';
import { useUserContext } from '../../contexts/UserContext';
import useProductV2Actions from './useProductV2Actions';
import styles from './styles.module.scss';
import {
  DEFAULT_PRODUCT_CATEGORY_ORDER,
  DEFAULT_TEXTURE_CATEGORY_ORDER,
  PRODUCT_PAGE_FILE_SUPPORTED_TYPES,
  PRODUCT_PAGE_GIF_SUPPORTED_TYPES,
  PRODUCT_PAGE_IMAGE_SUPPORTED_TYPES,
  PRODUCT_PAGE_VIDEO_SUPPORTED_TYPES,
  RENDER_STATUS,
  REQUEST_STATUSES,
} from '../../constants/constants';
import {
  AddPlusIcon,
  FavoriteIcon,
  FavoriteRedIcon,
  ShareProductIcon,
  UploadV2Icon,
  PresentationIcon,
  SettingsIcon,
  SearchBarIcon,
  ListV2Icon,
  CrossIcon,
  CycleIcon,
} from '../../assets/icons';
import {
  apiAddWhoCanAccessTypeValue,
  apiCheckIsPackProductViaId,
  apiCreatePublicUUID,
  apiCreatePublicUUIDTexture,
  apiEditTextureV2,
  apiGetProductCategoryImages,
  apiGetProductPageWhoCanAccessType,
  apiGetPropOptimizedV2,
  apiGetPropPublic,
  apiGetPublicProductPageWhoCanAccessType,
  apiGetPublicUUID,
  apiGetPublicUUIDTexture,
  apiGetRequest,
  apiGetTextureOptimizedV2,
  apiGetTexturePublic,
  GetProductShareStatus,
  GetTextureShareStatus,
  MakeProductPublicShare,
  MakeTexturePublicShare,
  UpdateProductPublicShare,
  UpdateTexturePublicShare,
} from '../../helpers/api';
import { Button } from 'react-bootstrap';
import { useApi } from '../../hooks/useApi';
import AddToCollectionPopup from '../../common/AddToCollectionPopup';
import { PageSearchBar } from '../../common/UniversalSearchForm/UniversalSearchForm';
import { useDispatch, useSelector } from 'react-redux';
import { setUploadedFiles } from '../../redux/slicers/appSlicer';
import { usePageMetaTags, usePageTitle } from '../../hooks/usePageTitle';
import { DotLottieReact } from '@lottiefiles/dotlottie-react';
import useCategoryActions from './useCategoryActions';
import { getAndSetCookieData, loadImageAsync } from '../../helpers/apiHelper';
import {
  decimals,
  parseFloatNaN,
  getProductPageType,
  SortArrayBySeq,
  isTextureProductPage,
} from '../../helpers/jsHelper';
import { DropdownMenu } from '../subscriptionComponents/DropdownMenu';
import { EditProductTextureDetailsPopupV2 } from './EditProductTextureDetailsPopupV2';
import { getCuratorSettings } from '../../api/curator';
import DotsIcon from '../../components/icons/dots';
import { selectActiveMeasureUnitsName } from '../../redux/slicers/admin/curatorSlicer';
import { convertCMToActiveUnits } from '../../helpers/curatorActions';
import { CONVERT_TO_CM, MEASURE_UNIT_LABEL } from '../../config/constant/unityConstants';
import { successToast, warningToast } from '../../helpers/toastHelper';
import {
  selectActiveView,
  selectProductInfo,
  selectShowEditorNudge,
  selectShowUpgradeMorePopup,
  selectTriggerProductCategoryUpdate,
  selectTriggerProductUpdate,
  setActiveView,
  setProductImageList,
  setProductInfo,
  setSharePublicLinkData,
  setShowEditorNudge,
  setShowUpgradeMorePopup,
  setTriggerProductCategoryUpdate,
  setTriggerProductUpdate,
} from '../../redux/slicers/productV2Slicer';
import { ElipsisTooltip } from '../../common/ElipsisTooltip';
const ListView = lazy(() => import('./ListView'));
const PresentationView = lazy(() => import('./PresentationView'));
import { navigate } from '../../helpers/projectHelper';
import { EditorNudge } from './EditorNudge';
import { ShareAndCopyLinkSection } from '../../common/SharePopup';
import UpgradeForMore from '../../components/popups/UpgradeForMore';
import {
  sendProductPageViewedData,
  shouldTrackPageViewed,
} from '../../components/EmailGating/EmailGating';
import { ShareProductPageModal, WHO_CAN_ACCESS_PAGE } from './ShareProductPageModal';
import { Mixpanel, MxEvent } from '../../common/Mixpanel';
import { getAppState } from '../../redux/store';
import { isAdminOrOwner } from '../../helpers/userHelper';
import { setAssignEditorPopOpen } from '../../redux/slicers/uiSlicer';

const isProcessing = (s) => {
  if (s === null || s === undefined) return false;
  return [RENDER_STATUS.STATUS_NOT_DONE, RENDER_STATUS.STATUS_PROCESSING]?.includes(s);
};

export const checkStatus = (c = {}) => {
  if (c?.category === getProductPageType().THREESIXTY) {
    return isProcessing(c?.multiviewergroup_details?.status);
  } else if (c?.category === getProductPageType().PRODUCTIMAGE) {
    return isProcessing(c?.render_image_details?.status);
  } else if (c?.category === getProductPageType().DIMENSIONSHOT) {
    return isProcessing(c?.render_image_details?.status);
  } else if (
    [getProductPageType().ARVIEWER, getProductPageType().THREEDVIEWER].includes(c?.category)
  ) {
    const productDetails = selectProductInfo(getAppState());
    return isProcessing("generate_ar_content_status" in productDetails ? productDetails?.generate_ar_content_status : c?.status);
  } else {
    return false;
  }
};

export const getLoaded360Images = async (images = [], checkForValidUrl = true) => {
  let loadedImages = [];
  if (checkForValidUrl) {
    await Promise.allSettled(
      images?.map(async (image) => {
        const res = await loadImageAsync(image?.image_file);
        if (res) {
          loadedImages.push(image);
        }
      })
    );
  } else {
    loadedImages = images;
  }
  return SortArrayBySeq(loadedImages, 'sequence_number');
};

export const AnimatedLoader = memo(({ id = null, show = true, style = {} }) => {
  if (!show) return null;

  return (
    <div key={id} id={id} style={style}>
      <DotLottieReact
        key={id}
        src="https://media.imagine.io/media/public/loader/imagine-loader.lottie"
        loop
        autoplay
        autoResizeCanvas
        speed={1.5}
      />
    </div>
  );
});

export const imagePlaceholder = 'https://via.placeholder.com/150';

let breadcrumbsList = [
  {
    label: 'Products',
    link: ADMIN_MY_PRODUCTS_3DMODAL_V2,
  },
  {
    label: 'My Models',
    link: ADMIN_MY_PRODUCTS_3DMODAL_V2,
  },
];

let breadcrumbsListTexture = [
  {
    label: 'Products',
    link: ADMIN_MY_PRODUCTS_3DMODAL_V2,
  },
  {
    label: 'My Textures',
    link: ADMIN_MY_PRODUCTS_TEXTURE_V2,
  },
];

// TODO: Need to make it more efficient & refactor the code
export const getAndSetProductCategoryContentInSession = (c = null, flag = false) => {
  const getSessionData = JSON.parse(sessionStorage.getItem('haveContentCategory')) || {};

  if (!c) {
    return getSessionData;
  }
  sessionStorage.setItem(
    'haveContentCategory',
    JSON.stringify({
      ...getSessionData,
      [c]: flag,
    })
  );
  return JSON.parse(sessionStorage.getItem('haveContentCategory')) || {};
};

// Here, i'm sorting the categories according to the available data if there is not changes in the order by the user.
// If there is any change in the order by the user, then the ordering is done based on the user backend data.
export const sortCategoryDataBasedOnContent = ({
  categories = [],
  setCategories = () => {},
  isTexture = false,
}) => {
  const CATEGORY_ORDER = isTexture
    ? DEFAULT_TEXTURE_CATEGORY_ORDER
    : DEFAULT_PRODUCT_CATEGORY_ORDER;

  const noChangeInOrder = categories.every(
    (i) => CATEGORY_ORDER?.[isTexture ? i?.texturetype : i?.producttype] === i?.seq_no
  );
  let c;
  if (noChangeInOrder) {
    const haveContentCategory = getAndSetProductCategoryContentInSession();
    if (Object.keys(haveContentCategory).some((i) => haveContentCategory[i])) {
      c = categories.map((i) => {
        if (haveContentCategory?.[isTexture ? i?.texturetype : i?.producttype]) {
          return {
            ...i,
            seq_no: CATEGORY_ORDER[isTexture ? i?.texturetype : i?.producttype],
          };
        }
        return {
          ...i,
          seq_no: isTexture ? 7 : 9, // keeping no data category at the end of the list
        };
      });
      setCategories?.(c);
    }
  }
};

function ProductDetailsV2({
  publicProductId = null,
  publicProductOrgId = null,
  isTexture = false,
  type = 'model',
  publicData = null,
}) {
  const routeParams = useParams();
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showSearchBox, setShowSearchBox] = useState(false);
  const [search, setSearch] = useState('');
  const acceptedFilesRef = useRef({});
  const debounceTimeout = useRef(null);
  const dispatch = useDispatch();
  const [showMainLoader, setShowMainLoader] = useState(false);
  const isPublicPage = publicProductId;
  const productId = publicProductId || parseInt(routeParams.id, 10);
  const { currentUser, isEditor, isGuestUser, isFreemiumUser } = useUserContext();
  const currentOrg = publicProductOrgId || currentUser?.organization;
  const { isListView, isDisplayView } = useSelector(selectActiveView);
  const [isFavourite, setIsFavourite] = useState(false);
  const [editInfo, setEditInfo] = useState(null);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const isProductCategoryUpdated = useSelector(selectTriggerProductCategoryUpdate);
  const productInfo = useSelector(selectProductInfo);
  const showEditorNudge = useSelector(selectShowEditorNudge);
  const triggerProductUpdate = useSelector(selectTriggerProductUpdate);
  const [isPackProduct, setIsPackProduct] = useState(false);
  const [isProductPackExpiry, setIsProductPackExpiry] = useState(false);
  const [publicRealProductId, setPublicRealProductId] = useState(null);
  const [productUnicodeId, setProductUnicodeId] = useState(null);
  const [orgProductUniqueCodeId, setOrgProductUniqueCodeId] = useState(null);
  const [showSharePopUp, setShowSharePopUp] = useState(false);
  const [isShareLoading, setIsShareLoading] = useState(false);
  const [publicShare, setPublicShare] = useState(false);
  const showUpgradeMorePopUp = useSelector(selectShowUpgradeMorePopup);
  const [shareAPIData, setShareAPIData] = useState(null);
  // const [productInfo, setProductInfo] = useState(null);
  const [settingsLoading, setSettingsLoading] = useState(false);
  const timeoutRef = useRef(null);
  const isStoppedRef = useRef(false);
  const prevCountRef = useRef(0);
  const sameCountRef = useRef(0);
  const { data, loading, refetch, update } = useApi(
    {
      api: isPublicPage
        ? isTexture
          ? apiGetTexturePublic
          : apiGetPropPublic
        : isTexture
        ? apiGetTextureOptimizedV2
        : apiGetPropOptimizedV2,
      id: productId,
      passObjectAsArgument: Boolean(isPublicPage),
      fetchBeforeMounting: true,
      params: `is_guest_user=${isGuestUser()}`,
      isProductPage: true,
    },
    [currentOrg]
  );

  const isCurrentUserOwner = useMemo(() => {
    if (!currentUser || !data) return false;
    const isSameOrg = +currentUser.organization === +data?.owner_organization;
    const isMember =
      +data?.member === +currentUser?.id || +currentUser?.id === +data?.member_details?.member_id;
    return isEditor || isMember || (isAdminOrOwner(currentUser) && isSameOrg);
  }, [currentUser, data?.owner_organization, data?.member, isEditor]);

  // Set the default view to LIST view for non-public pages when the component mounts
  useLayoutEffect(() => {
    if (!isPublicPage) {
      dispatch(setActiveView('LIST'));
    }
  }, [isPublicPage]);

  useEffect(() => {
    dispatch(setShowEditorNudge(false));
    sessionStorage.removeItem('haveContentCategory');

    return () => {
      dispatch(setActiveView('DISPLAY'));
      sessionStorage.removeItem('haveContentCategory');
    };
  }, []);

  useEffect(() => {
    if (isPublicPage || !data?.id || data?.generate_images_content_status !== 2) {
      return;
    }

    window.$productImageList = 'initializing';
    isStoppedRef.current = false;
    sameCountRef.current = 0;

    const fetchImages = async () => {
      if (isStoppedRef.current) return;

      try {
        const params = {
          sort: 'is_archive,-order',
          product: productId,
        };

        const res = await apiGetProductCategoryImages({
          id: productId,
          params,
          page: 1,
          perPage: 100,
          time: new Date()?.getTime(),
        });

        const currentCount = res?.results?.length || 0;
        const prevCount = prevCountRef.current;

        if (currentCount > 0) {
          dispatch(setProductImageList(res.results));
          window.$productImageList = res.results;
        }

        if (currentCount > 0) {
          if (currentCount === prevCount) {
            sameCountRef.current += 1;
            if (sameCountRef.current >= 3) {
              isStoppedRef.current = true;
              return;
            }
          } else {
            sameCountRef.current = 0;
          }
          prevCountRef.current = currentCount;
        }

        timeoutRef.current = setTimeout(fetchImages, 1500);
      } catch (err) {
        timeoutRef.current = setTimeout(fetchImages, 1500);
      }
    };

    timeoutRef.current = setTimeout(fetchImages, 2000);

    return () => {
      isStoppedRef.current = true;
      clearTimeout(timeoutRef.current);
      window.$productImageList = null;
    };
  }, [data?.id, isPublicPage, productId]);

  useEffect(() => {
    if (!currentUser || isPublicPage || isTexture) return;
    if (data && +data?.owner_organization === +currentUser?.organization) {
      checkIsPackProduct();
    } else {
      setIsPackProduct(false);
    }
  }, [productId, data, currentUser]);

  const checkIsPackProduct = async () => {
    const result = await apiCheckIsPackProductViaId(isPublicPage ? data?.id : productId);
    if (
      result &&
      result?.status &&
      data?.request_details?.id &&
      data?.request_details?.status === REQUEST_STATUSES.LIVE_ON_APP.value
    ) {
      const request = await apiGetRequest(data?.request_details?.id);
      if (request) {
        setIsProductPackExpiry(
          request?.model_request?.packinfo_details?.every((a) => a?.is_expiry)
        );
      }
      setIsPackProduct(result?.data?.is_pack);
    } else {
      setIsPackProduct(false);
    }
  };

  useEffect(async () => {
    if (data?.id && !loading) {
      let _productDetails = { ...data } || {};
      setPublicRealProductId(_productDetails?.id);
      if (currentUser) {
        const getFunc = isTexture ? apiGetPublicUUIDTexture : apiGetPublicUUID;
        const response = await getFunc(
          `?${isTexture ? 'texture' : 'product'}=${
            _productDetails?.id
          }&is_guest_user=${isGuestUser()}`
        );
        if (response?.results?.length) {
          setOrgProductUniqueCodeId(response.results[0]?.unique_code);
          dispatch(setSharePublicLinkData(response.results[0]));
        } else {
          const formData = new FormData();
          formData.append(isTexture ? 'texture' : 'product', _productDetails?.id);
          const createFunc = isTexture ? apiCreatePublicUUIDTexture : apiCreatePublicUUID;
          const response = await createFunc(formData);
          if (response?.status) {
            setOrgProductUniqueCodeId(response?.data?.unique_code);
            dispatch(setSharePublicLinkData(response?.data));
          }
        }
        const getStatus = isTexture ? GetTextureShareStatus : GetProductShareStatus;
        const res = await getStatus(_productDetails?.id);
        if (res?.results?.length) {
          setShareAPIData(res?.results[0] || null);
          window.$shareItem = res?.results?.[0];
          window.$shareFlag = res?.results[0]?.is_public;
          if (res?.results[0]?.is_public) {
            setPublicShare(true);
          }
        } else {
          window.$shareItem = null;
          window.$shareFlag = false;
        }
      } else {
        setOrgProductUniqueCodeId(routeParams.id);
        dispatch(setSharePublicLinkData({ unique_code: routeParams.id }));
      }
      setProductUnicodeId(_productDetails?.unicode);
      dispatch(setProductInfo(_productDetails));
    }
  }, [data, loading]);

  // This `useLayoutEffect` hook is responsible for handling the loading and data state of the product details.
  // It dispatches the `setProductInfo` action with the `data` object if it exists, otherwise it sets a timeout to navigate to the appropriate admin product page after 10 milliseconds.
  // The cleanup function clears the timeout if it exists.
  useLayoutEffect(() => {
    let timerId;
    if (!loading) {
      if (data) dispatch(setProductInfo(data));
      else {
        dispatch(setAssignEditorPopOpen({ redirectUrl: window.location.pathname }));
        timerId = setTimeout(
          () =>
            navigate(isTexture ? ADMIN_MY_PRODUCTS_TEXTURE_V2 : ADMIN_MY_PRODUCTS_3DMODAL_V2, {
              replace: true,
            }),
          10
        );
      }
    }
    return () => {
      if (timerId) clearTimeout(timerId);
    };
  }, [loading, data, isTexture]);

  const fetchProductDetails = async (flag = true) => {
    const api = isTexture ? apiGetTextureOptimizedV2 : apiGetPropOptimizedV2;
    const response = await api(productInfo?.id, `is_guest_user=${isGuestUser()}`);
    if (flag) {
      dispatch(setProductInfo(response));
    }
    if (!flag) {
      return response;
    }
  };

  // This `useEffect` hook is responsible for fetching the product details when the `triggerProductUpdate` state changes.
  // It checks if the `productId` matches the `triggerProductUpdate`, and if so, it calls the `fetchProductDetails` function and dispatches an action to reset the `triggerProductUpdate` state to `null`.
  useEffect(() => {
    if (+productId === +triggerProductUpdate) {
      fetchProductDetails();
      dispatch(setTriggerProductUpdate(null));
    }
  }, [triggerProductUpdate]);

  usePageTitle(productInfo?.name);

  usePageMetaTags({
    title: productInfo?.name,
    description: productInfo?.description,
    thumbnail: productInfo?.thumbnail,
  });

  useEffect(() => {
    dispatch(setUploadedFiles({}));
  }, []);

  useEffect(() => {
    let timerId;
    // The tracking works only on public page
    if (isPublicPage && shouldTrackPageViewed() && productInfo?.id && !loading) {
      const productUUID = routeParams.id;

      timerId = setTimeout(() => {
        // Check if one hour has passed since the last page view for the given product.
        const isOneHourPassed = getAndSetCookieData({
          cookieName: `ppv_${productUUID}`, // ppv - product page viewed
        });

        // If one hour has not passed since the last view, send the page view data.
        if (!isOneHourPassed) {
          sendProductPageViewedData({
            productUUID: productUUID,
          });
        }
      }, 5000);
    }
    return () => {
      if (timerId) clearTimeout(timerId);
    };
  }, [isPublicPage, productInfo, loading]);

  useEffect(() => {
    if (productInfo) setIsFavourite(productInfo?.is_favourite);
  }, [productInfo]);

  const {
    favouriteLoading,
    handleToggleFavourite,
    handleAddCollection,
    handleShareProduct,
    handleShareProductPage,
  } = useProductV2Actions({
    productInfo: {
      ...productInfo,
      is_favourite: isFavourite,
      is_texture: isTexture,
      is_v2: true,
    },
    isTexture,
  });

  const {
    list: categories,
    setList: setCategories,
    loading: categoriesLoading,
    fetchProductCategories,
  } = useCategoryActions({
    productId: publicRealProductId,
    productInfo,
    isTexture,
    params: `?is_enabled=true`,
    isPublicPage,
  });

  useEffect(() => {
    // calling fetchProductCategories only when product category is updated from the list view
    if (isProductCategoryUpdated) {
      fetchProductCategories();
      dispatch(setTriggerProductCategoryUpdate(false));
    }
  }, [isProductCategoryUpdated]);

  const handleUpload = (data) => {
    if (!isGuestUser() && isFreemiumUser()) {
      dispatch(setShowUpgradeMorePopup(true));
      return;
    }
    if (isGuestUser() && !isEditor) {
      warningToast('Permission denied');
      return;
    }
    acceptedFilesRef.current.uploadType = data?.[isTexture ? 'texturetype' : 'producttype'];

    acceptedFilesRef.current.acceptedFiles =
      data?.[isTexture ? 'texturetype' : 'producttype'] === getProductPageType().GIF
        ? PRODUCT_PAGE_GIF_SUPPORTED_TYPES
        : getProductPageType().PRODUCTIMAGE === data?.[isTexture ? 'texturetype' : 'producttype'] ||
          getProductPageType().DIMENSIONSHOT === data?.[isTexture ? 'texturetype' : 'producttype']
        ? PRODUCT_PAGE_IMAGE_SUPPORTED_TYPES
        : data?.[isTexture ? 'texturetype' : 'producttype'] === getProductPageType().VIDEO
        ? PRODUCT_PAGE_VIDEO_SUPPORTED_TYPES
        : null;

    if (acceptedFilesRef.current?.acceptedFiles) setShowUploadModal(true);
  };

  const handleAddBreadcrumName = () => {
    const list = isTexture ? breadcrumbsListTexture : breadcrumbsList;
    let path = list.slice();
    if (productInfo) {
      path.push({
        label: productInfo?.name,
      });
    }
    return path || [];
  };

  const breadcrumbsMenu = useMemo(
    () =>
      window.innerWidth < 768 ? (
        <ElipsisTooltip title={productInfo?.name}>
          {({ ref, onMouseEnter, onMouseLeave }) => (
            <h2
              ref={ref}
              onMouseEnter={onMouseEnter}
              onMouseLeave={onMouseLeave}
              className={cx(styles.productTitle, 'ellipsis')}>
              {productInfo?.name}
            </h2>
          )}
        </ElipsisTooltip>
      ) : (
        <Breadcrumbs list={handleAddBreadcrumName()} />
      ),
    [productInfo]
  );

  // const topMenuList = useMemo(
  //   () => [
  //     {
  //       label: 'Favourite',
  //       icon: !isFavourite ? <FavoriteIcon /> : <FavoriteRedIcon />,
  //       onClick: () =>
  //         handleToggleFavourite({
  //           callback: setIsFavourite(!isFavourite),
  //         }),
  //       show: true,
  //     },
  //     {
  //       label: 'Share',
  //       icon: <ShareProductIcon />,
  //       onClick: handleShareProduct,
  //       show: true,
  //     },
  //     {
  //       label: 'Collection',
  //       icon: <AddPlusIcon />,
  //       onClick: handleAddCollection,
  //       show: true,
  //     },
  //     // {
  //     //   label: 'Edit',
  //     //   icon: <EditPensilIcon />,
  //     //   onClick: toggleEdit,
  //     //   show: isEditor ? true : false,
  //     // },
  //     // {
  //     //   label: 'AI Video',
  //     //   icon: <AIVideoIcon />,
  //     //   onClick: generateAutomaticAIVideoPreview,
  //     //   show: productInfo?.final_render_status?.status !== RENDER_STATUS.STATUS_DONE ? true : false,
  //     // },
  //   ],
  //   [isFavourite]
  // );

  const handleSearch = (val) => {
    if (!val) {
      setSearch(val);
    }

    // Clear any existing timeout
    if (debounceTimeout.current) {
      clearTimeout(debounceTimeout.current);
    }

    // Set a new timeout
    debounceTimeout.current = setTimeout(() => {
      setSearch(val);
    }, 500);
  };

  const handleAllUpload = () => {
    acceptedFilesRef.current.acceptedFiles = PRODUCT_PAGE_FILE_SUPPORTED_TYPES;
    acceptedFilesRef.current.uploadType = null;
    setShowUploadModal(true);
  };

  const activeTab = {};
  const eventProperties = useMemo(
    () => ({
      tab_name: activeTab?.display_name,
      product_page_view: isDisplayView ? 'Display Mode' : 'Content List',
      ...(productInfo
        ? isTexture
          ? {
              texture_id: productInfo?.id,
              texture_name: productInfo?.name,
            }
          : {
              product_id: productInfo?.id,
              product_name: productInfo?.name,
            }
        : {}),
    }),
    [activeTab, productInfo, isTexture, isDisplayView]
  );

  const menuList = useMemo(
    () => [
      {
        id: 1,
        title: 'Add to Collection',
        hide: isGuestUser(),
        icon: AddPlusIcon,
        mxEvent: MxEvent.CTA_CLICKED,
        eventProps: eventProperties,
        onClick: (v) => {
          handleAddCollection(v);
          setShowMobileMenu(false);
        },
      },
      {
        id: 2,
        title: 'Favourite',
        hide: isGuestUser(),
        icon: !isFavourite ? FavoriteIcon : FavoriteRedIcon,
        mxEvent: MxEvent.CTA_CLICKED,
        eventProps: eventProperties,
        onClick: () => {
          handleToggleFavourite({
            callback: setIsFavourite(!isFavourite),
          });
          setShowMobileMenu(false);
        },
      },
      {
        id: 3,
        title: 'Upload',
        icon: UploadV2Icon,
        mxEvent: MxEvent.CTA_CLICKED,
        eventProps: eventProperties,
        onClick: (v) => {
          if (!isGuestUser() && isFreemiumUser()) {
            dispatch(setShowUpgradeMorePopup(true));
            return;
          }
          if (isGuestUser() && !isEditor) {
            warningToast('Permission denied');
            return;
          }
          handleAllUpload(v);
          setShowMobileMenu(false);
        }, // TODO: Check with the team about to hide the upload for the Freemium user only or Should we hide the upload for the Old Subscription & Old Premium user.
      },
    ],
    [productInfo, isFavourite, currentOrg, isEditor]
  );

  const mobileMenuList = useMemo(
    () => [
      ...menuList,
      {
        id: 4,
        title: 'Settings',
        icon: SettingsIcon,
        mxEvent: MxEvent.CTA_CLICKED,
        eventProps: eventProperties,
        onClick: () => {
          onSettingsClick();
          setShowMobileMenu(false);
        },
        // hide: isGuestUser(), // commenting this code as now we are allowing the Guest user to add the Product page tags
      },
      {
        // TODO: Need to implement Create content button working
        id: 5,
        title: 'Generate Visuals',
        icon: CycleIcon,
        mxEvent: MxEvent.CTA_CLICKED,
        eventProps: eventProperties,
        onClick: () => {
          setShowMobileMenu(false);
        },
        hide: true, // TODO: Make it unhide when work on it
      },
      {
        id: 6,
        title: 'Share',
        icon: ShareProductIcon,
        mxEvent: MxEvent.CTA_CLICKED,
        eventProps: eventProperties,
        onClick: (v) => {
          isGuestUser() ? setShowSharePopUp(true) : handleShareProduct(v);
          setShowMobileMenu(false);
        },
        type: 'button',
        hide: true, // TODO: Make it unhide when work on it
      },
      {
        id: 7,
        title: isDisplayView ? 'Content List' : 'Display Mode',
        icon: isDisplayView ? ListV2Icon : PresentationIcon,
        mxEvent: MxEvent.CTA_CLICKED,
        eventProps: {
          cta_type: isDisplayView ? 'Content List' : 'Display Mode',
          ...eventProperties,
        },
        onClick: () => {
          dispatch(setActiveView(isDisplayView ? 'LIST' : 'DISPLAY'));
          setShowMobileMenu(false);
        },
      },
    ],
    [showMobileMenu, menuList]
  );

  const handleShareFile = async () => {
    setIsShareLoading(true);
    const payload = {
      [isTexture ? 'texture' : 'product']: productInfo?.id,
      is_publish: true,
      is_public: !publicShare,
    };
    let res = null;
    if (shareAPIData && Object.keys(shareAPIData)?.length) {
      const updateFunc = isTexture ? UpdateTexturePublicShare : UpdateProductPublicShare;
      res = await updateFunc(payload);
    } else {
      const makeFunc = isTexture ? MakeTexturePublicShare : MakeProductPublicShare;
      res = await makeFunc(payload);
    }
    if (res?.status && res?.data) {
      window.$shareItem = res?.data;
      window.$shareFlag = res?.data?.is_public;
      setShareAPIData(res?.data);
      const getFunc = isTexture ? apiGetPublicUUIDTexture : apiGetPublicUUID;
      const response = await getFunc(`?${isTexture ? 'texture' : 'product'}=${productInfo?.id}`);
      if (response?.results?.length) {
        setOrgProductUniqueCodeId(response.results[0]?.unique_code);
        dispatch(setSharePublicLinkData(response.results[0]));
      } else {
        const formData = new FormData();
        formData.append(isTexture ? 'texture' : 'product', productInfo?.id);
        const createFunc = isTexture ? apiCreatePublicUUIDTexture : apiCreatePublicUUID;
        const response = await createFunc(formData);
        if (response?.status) {
          setOrgProductUniqueCodeId(response?.data?.unique_code);
          dispatch(setSharePublicLinkData(response?.data));
        }
      }
      setPublicShare(!publicShare);
    } else {
      window.$shareItem = null;
      window.$shareFlag = false;
      setOrgProductUniqueCodeId(null);
      dispatch(setSharePublicLinkData(null));
    }
    setIsShareLoading(false);
  };

  const onSettingsClick = async () => {
    setEditInfo(productInfo);
    setSettingsLoading(true);
    const info = await fetchProductDetails(false);
    setEditInfo(info);
    setSettingsLoading(false);
  };

  const getProductWhoCanAccessType = async () => {
    const productIds = publicData?.product || publicData?.texture || productId;
    const params = `${isTextureProductPage() ? 'texture' : 'product'}=${productIds}`;
    const APIFunction = publicData
      ? apiGetPublicProductPageWhoCanAccessType
      : apiGetProductPageWhoCanAccessType;
    const res = await APIFunction(params);
    if (!res?.results?.length) {
      const payload = {
        [isTextureProductPage() ? 'texture' : 'product']: productIds,
        product_page_share_access: WHO_CAN_ACCESS_PAGE.INVITED,
      };
      const data = await apiAddWhoCanAccessTypeValue(payload);
      dispatch(setSharePublicLinkData({ ...data?.data, mainId: data?.data?.id }));
    } else {
      dispatch(
        setSharePublicLinkData({ ...res?.results?.[0], mainId: res?.results?.[0]?.id } || {})
      );
    }
  };

  useEffect(() => {
    if (productId) {
      getProductWhoCanAccessType();
    }
  }, [productId]);

  return (
    <>
      <div
        className={cx('product-details position-relative', styles.productDetails)}
        id="product-container">
        <div
          className="d-flex justify-content-between align-items-center gap-2"
          style={
            window.innerWidth < 768
              ? {
                  padding: '0.5rem 0.5rem 0.5rem 1.5rem',
                }
              : {}
          }>
          {!isPublicPage && breadcrumbsMenu}
          {!isPublicPage && (
            <div
              className={cx(
                'd-flex flex-wrap gap-2 px-md-4 px-2'
                // showMainLoader ? styles.disableHeader : ''
              )}
              style={loading ? { opacity: 0 } : { opacity: 1 }}>
              {/* showing this menu in mobile view */}
              {mobileMenuList?.filter((_item) => !_item?.hide)?.length && (
                <Button
                  className={cx(styles.btn, styles.mobileMenuItems)}
                  style={{
                    zIndex: 20,
                    border: 'none',
                  }}
                  onClick={() => setShowMobileMenu(!showMobileMenu)}>
                  <DotsIcon />
                </Button>
              )}
              {isCurrentUserOwner && (
                <Button
                  className={cx(styles.bigBtn, styles.primary, styles.mobileMenuItems)}
                  onClick={(e) => {
                    Mixpanel.track(true, MxEvent.CTA_CLICKED, {
                      cta_type: 'Share',
                      ...eventProperties,
                    });
                    isGuestUser()
                      ? handleShareProductPage(e, productInfo)
                      : // ? setShowSharePopUp(true)
                        handleShareProductPage(e, productInfo); // handleShareProduct(e);
                  }}>
                  <ShareProductIcon /> <span className={styles.text}>Share</span>
                </Button>
              )}
              {/* showing this in desktop view */}
              <div className={cx(styles.desktopMenuItems)}>
                {isListView &&
                  (!showSearchBox && !search ? (
                    <Button className={styles.btn} onClick={() => setShowSearchBox(true)}>
                      <SearchBarIcon />
                    </Button>
                  ) : (
                    <PageSearchBar
                      inputClassName={styles.searchInputClassName}
                      searchBarClassName="d-block"
                      designV2
                      onSearchChange={(val) => handleSearch(val)}
                      onBlur={() => setShowSearchBox(false)}
                      search={search}
                      hideTags={true}
                      autoFocus={true}
                      triggerOnType={true}
                    />
                  ))}
                {menuList?.filter((_item) => !_item?.hide)?.length && (
                  <Button
                    className={cx(styles.btn, styles.menuContainer)}
                    style={{
                      zIndex: 20,
                    }}>
                    <DropdownMenu
                      className={cx('m-0', styles.menu)}
                      menu={menuList}
                      button={<DotsIcon className={styles.dotsIcon} />}
                    />
                  </Button>
                )}
                {/* {!isGuestUser() && ( // commenting this code as now we are allowing the Guest user to add the Product page tags */}
                <Button
                  className={styles.btn}
                  onClick={() => {
                    setEditInfo(productInfo);
                    Mixpanel.track(true, MxEvent.CTA_CLICKED, {
                      cta_type: 'Settings',
                      ...eventProperties,
                    });
                  }}>
                  <SettingsIcon />
                </Button>
                {/* )} */}
                <div className={styles.switchView}>
                  <Button
                    className={cx(styles.switch, isListView ? styles.active : '')}
                    onClick={() => {
                      dispatch(setActiveView('LIST'));

                      Mixpanel.track(true, MxEvent.CTA_CLICKED, {
                        cta_type: 'Content List',
                        ...eventProperties,
                      });
                    }}>
                    <ListV2Icon /> <span className={styles.text}>Content List</span>
                  </Button>
                  <Button
                    className={cx(styles.switch, isDisplayView ? styles.active : '')}
                    onClick={() => {
                      dispatch(setActiveView('DISPLAY'));

                      Mixpanel.track(true, MxEvent.CTA_CLICKED, {
                        cta_type: 'Display Mode',
                        ...eventProperties,
                      });
                    }}>
                    <PresentationIcon /> <span className={styles.text}>Display Mode</span>
                  </Button>
                </div>
                {/* TODO: Make it unhide when work on it */}
                {/* <Button onClick={() => {}} className={styles.bigBtn}>
                  <PlusIcon /> <span className={styles.text}>Create content</span>
                </Button> */}

                {/* TODO: Commenting share button as it is not developered yet
                <Button
                  className={cx(styles.bigBtn, styles.primary)}
                  onClick={(e) => {
                    Mixpanel.track(true, MxEvent.CTA_CLICKED, {
                      cta_type: 'Share',
                      ...eventProperties,
                    });
                    isGuestUser() ? setShowSharePopUp(true) : handleShareProduct(e);
                  }}>
                  <ShareProductIcon /> <span className={styles.text}>Share</span>
                </Button> */}
                {isCurrentUserOwner && (
                  <Button
                    className={cx(styles.bigBtn, styles.primary)}
                    onClick={(e) => {
                      isGuestUser()
                        ? handleShareProductPage(e, productInfo)
                        : handleShareProductPage(e, productInfo); // handleShareProduct(e);
                    }}>
                    <ShareProductIcon /> <span className={styles.text}>Share page</span>
                  </Button>
                )}
              </div>
            </div>
          )}
        </div>
        {showMobileMenu && (
          <MobileMenu menu={mobileMenuList} onClose={() => setShowMobileMenu(false)} />
        )}
        <Suspense
          // fallback={<Loader isV2={true} loading position="absolute" hideLoadingText={true} />}
          fallback={<></>}>
          <PresentationView
            className={`${isDisplayView ? 'd-block' : 'd-none'} ${
              isPublicPage ? styles.heightFull : ''
            }`}
            loading={loading || categoriesLoading}
            show={isDisplayView}
            acceptedFiles={acceptedFilesRef.current}
            {...{
              categories,
              productId,
              productInfo,
              handleShareProduct,
              handleUpload,
              showUploadModal,
              setShowUploadModal,
              isTexture,
              isPublicPage,
              productUnicodeId,
              orgProductUniqueCodeId,
              isPackProduct: isPackProduct && !isProductPackExpiry,
              currentOrg,
              publicShare,
            }}
          />
          {!loading && (
            <ProductInfoView
              info={isDisplayView ? productInfo : null}
              isTexture={isTexture}
              isPublicPage={isPublicPage}
            />
          )}
          {!isPublicPage && !loading && (
            <ListView
              className={isListView ? 'd-block' : 'd-none'}
              {...{
                productInfo,
                productId,
                handleShareProduct,
                show: isListView,
                handleUpload,
                showUploadModal,
                setShowUploadModal,
                acceptedFiles: acceptedFilesRef.current,
                search,
                showSearchBox,
                showMainLoader,
                setShowMainLoader,
                isTexture,
                publicShare,
                orgProductUniqueCodeId,
              }}
            />
          )}
        </Suspense>
        {!isPublicPage && <AddToCollectionPopup />}
      </div>
      {editInfo && !isPublicPage && (
        <EditProductAndTextureInfo
          {...{
            productId: publicRealProductId,
            show: Boolean(editInfo),
            data: editInfo,
            loading: loading || settingsLoading,
            onClose: () => {
              setEditInfo(null);
              setShowMobileMenu(false);
            },
            thumbnail: editInfo?.thumbnail,
            onUpdateSuccess: () => {
              refetch();
              setEditInfo(null);
              setShowMobileMenu(false);
              successToast(`${isTexture ? 'Texture' : 'Product'} updated successfully`);
            },
            isTexture,
          }}
        />
      )}
      {!isPublicPage && showEditorNudge && <EditorNudge />}
      {showSharePopUp && (
        <ShareAndCopyLinkSection
          isDisabled={!isEditor}
          isShareLoading={isShareLoading}
          publicShare={publicShare}
          unicode={orgProductUniqueCodeId}
          setShowSharePopUp={setShowSharePopUp}
          productId={productId}
          isTexture={isTexture}
          handleToggleChange={handleShareFile}
        />
      )}
      {showUpgradeMorePopUp && (
        <UpgradeForMore
          openInNewTab
          onClose={() => {
            dispatch(setShowUpgradeMorePopup(false));
          }}
        />
      )}
      {!isPublicPage && <ShareProductPageModal />}
    </>
  );
}

export default memo(ProductDetailsV2);

const ProductInfoView = ({ info = null, isTexture = false, isPublicPage }) => {
  const [showInfo, setShowInfo] = useState(window.innerWidth <= 1024);

  // this is for the texture only
  const dispatch = useDispatch();
  const { currentUser } = useUserContext();
  const measureUnits = useSelector(selectActiveMeasureUnitsName);
  const DEFAULT_VALUE = decimals(convertCMToActiveUnits(CONVERT_TO_CM), 2);

  const parseTextureSize = (item) => {
    return decimals(convertCMToActiveUnits(parseFloatNaN(item || DEFAULT_VALUE)), 2);
  };

  useEffect(() => {
    if (isTexture && !isPublicPage) {
      dispatch(getCuratorSettings({ userId: currentUser?.user?.id }));
    }

    window.addEventListener('resize', () => setShowInfo(window.innerWidth <= 1024));
    return () => window.removeEventListener('resize', () => setShowInfo(window.innerWidth <= 1024));
  }, [isTexture]);

  if (!info || !showInfo) return null;

  let showXlabelFirst = false;

  if (info?.dim_x && info?.dim_z && info?.dim_x >= info?.dim_z) {
    showXlabelFirst = true;
  } else {
    showXlabelFirst = false;
  }

  return (
    <div className={styles.productInfo}>
      <h2 className={styles.productTitle}>{info?.name}</h2>
      {info?.description ? <p className={styles.text}>{info?.description}</p> : null}
      {info?.price && (
        <div className={styles.info}>
          <label>PRICE</label>
          <p className={styles.text}>${info?.price}</p>
        </div>
      )}
      {info?.sku_id && (
        <div className={styles.info}>
          <label>SKU ID</label>
          <p className={styles.text}>{info?.sku_id}</p>
        </div>
      )}
      {info?.dim_x || info?.dim_y || info?.dim_z ? (
        <div className={styles.info}>
          <label>Dimensions</label>
          <p className={styles.text}>
            {`${showXlabelFirst ? info?.dim_x || 0 : info?.dim_z || 0}x${
              showXlabelFirst ? info?.dim_z || 0 : info?.dim_x || 0
            }x${info?.dim_y || 0}`}{' '}
            Inches
          </p>
        </div>
      ) : info?.dimensions ? (
        <div className={styles.info}>
          <label>Dimensions</label>
          <p className={styles.text}>{info?.dimensions} Inches</p>
        </div>
      ) : null}
      {info?.width && (
        <div className={styles.info}>
          <label>Width ({MEASURE_UNIT_LABEL[measureUnits] || ''})</label>
          <p className={styles.text}>{parseTextureSize(parseFloat(info?.width))}</p>
        </div>
      )}
      {info?.height && (
        <div className={styles.info}>
          <label>Height ({MEASURE_UNIT_LABEL[measureUnits] || ''})</label>
          <p className={styles.text}>{parseTextureSize(parseFloat(info?.height))}</p>
        </div>
      )}
      {info?.rotation && (
        <div className={styles.info}>
          <label>Rotation</label>
          <p className={styles.text}>{parseInt(info?.rotation)}</p>
        </div>
      )}
    </div>
  );
};

const MobileMenu = ({ menu = [], onClose }) => {
  if (!menu.length) return null;

  const triggerEvent = (item) => {
    if (item?.mxEvent) {
      Mixpanel.track(true, item?.mxEvent, {
        cta_type: item?.title,
        ...(item?.eventProps || {}),
      });
    }
  };

  return (
    <div className={styles.overlay}>
      <div className={styles.floatingWrapper}>
        <Button className={cx(styles.btn, styles.dark, styles.overlayCloseBtn)} onClick={onClose}>
          <CrossIcon />
        </Button>
        <ul className={styles.menuList}>
          {menu.map((item, i) => {
            const Icon = item?.icon;
            const isButton = item?.type === 'button';

            if (item?.hide) return null;

            if (isButton) {
              return (
                <li key={item?.id} className={cx(styles.menuItem, styles.menuBtn)}>
                  <Button
                    key={item?.id || i}
                    className={cx(
                      styles.bigBtn,
                      styles.primary,
                      item?.initialValue ? styles.cursorDefault : ''
                    )}
                    loading={item?.loading}
                    color={'primary'}
                    onClick={() => {
                      triggerEvent(item);
                      item?.onClick?.();
                    }}>
                    {Icon && <Icon />}
                    <span className={styles.text}>{item?.title}</span>
                  </Button>
                </li>
              );
            }

            return (
              <li
                key={item?.id}
                className={styles.menuItem}
                onClick={() => {
                  triggerEvent(item);
                  item?.onClick?.();
                }}>
                <span className={styles.icon}>
                  <Icon />
                </span>
                <span className={styles.text}>{item?.title}</span>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
};

const EditProductAndTextureInfo = ({
  productId,
  show,
  data,
  loading,
  onClose,
  thumbnail,
  onUpdateSuccess,
  isTexture,
}) => {
  if (!data) return null;

  if (window.innerWidth <= 1350) {
    return (
      <div className={cx(styles.overlay, styles.mobileOverlay)}>
        <Button className={cx(styles.btn, styles.dark, styles.overlayCloseBtn)} onClick={onClose}>
          <CrossIcon />
        </Button>

        <EditProductTextureDetailsPopupV2
          className={styles.floatingSettingWrapper}
          placement="bottom"
          title="Settings"
          {...{
            productId,
            data,
            show,
            onClose,
            loading,
            thumbnail,
            onUpdateSuccess,
            isTexture,
          }}
          {...(isTexture
            ? {
                saveApi: apiEditTextureV2,
              }
            : {})}
        />
      </div>
    );
  }

  return (
    <EditProductTextureDetailsPopupV2
      {...{
        productId,
        data,
        show,
        onClose,
        loading,
        thumbnail,
        onUpdateSuccess,
        isTexture,
      }}
      {...(isTexture
        ? {
            saveApi: apiEditTextureV2,
          }
        : {})}
    />
  );
};
